import { createContext, useContext, useRef, useState, useEffect, useCallback, ReactNode } from 'react';

export type MusicTrack = 'ambient' | 'fight' | 'relax' | 'underground' | 'victory';

const MUSIC_PATHS: Record<MusicTrack, string> = {
  ambient: '/sounds/DW ambient.mp3',
  fight: '/sounds/DW fight.mp3',
  relax: '/sounds/DW relax.mp3',
  underground: '/sounds/DW underground.mp3',
  victory: '/sounds/DW victory.mp3',
};

interface AudioContextType {
  isMusicPlaying: boolean;
  currentTrack: MusicTrack;
  volume: number;
  soundEnabled: boolean;
  toggleMusic: () => void;
  playMusic: () => void;
  pauseMusic: () => void;
  changeTrack: (track: MusicTrack) => void;
  setVolume: (v: number) => void;
  toggleSoundEnabled: () => void;
  playDiceSound: () => void;
  playHitSound: () => void;
  playVictorySound: () => void;
}

const AudioContext = createContext<AudioContextType | null>(null);

interface AudioProviderProps {
  children: ReactNode;
}

export function AudioProvider({ children }: AudioProviderProps) {
  const bgMusicRef = useRef<HTMLAudioElement | null>(null);
  const [isMusicPlaying, setIsMusicPlaying] = useState(false);
  const [currentTrack, setCurrentTrack] = useState<MusicTrack>('ambient');
  const [volume, setVolumeState] = useState(0.3);
  const [soundEnabled, setSoundEnabledState] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);

  // Initialize from localStorage on mount
  useEffect(() => {
    const savedVolume = localStorage.getItem('dw_music_volume');
    const savedSoundEnabled = localStorage.getItem('dw_sound_enabled');
    const savedTrack = localStorage.getItem('dw_current_track');

    if (savedVolume) setVolumeState(parseFloat(savedVolume));
    if (savedSoundEnabled !== null) setSoundEnabledState(savedSoundEnabled === 'true');
    if (savedTrack && MUSIC_PATHS[savedTrack as MusicTrack]) {
      setCurrentTrack(savedTrack as MusicTrack);
    }

    setIsInitialized(true);
  }, []);

  // Create/update audio element when track changes
  useEffect(() => {
    if (!isInitialized) return;

    // Clean up previous audio
    if (bgMusicRef.current) {
      bgMusicRef.current.pause();
      bgMusicRef.current.src = '';
      bgMusicRef.current = null;
    }

    const audio = new Audio(MUSIC_PATHS[currentTrack]);
    audio.loop = true;
    audio.volume = volume;
    audio.preload = 'auto';
    bgMusicRef.current = audio;

    // Save current track to localStorage
    localStorage.setItem('dw_current_track', currentTrack);

    if (isMusicPlaying) {
      audio.play().catch((err) => {
        console.log('Auto-play prevented:', err);
        setIsMusicPlaying(false);
      });
    }

    // Cleanup on unmount or track change
    return () => {
      if (bgMusicRef.current) {
        bgMusicRef.current.pause();
        bgMusicRef.current.src = '';
      }
    };
  }, [currentTrack, isInitialized]);

  // Update volume on audio element
  useEffect(() => {
    if (bgMusicRef.current) {
      bgMusicRef.current.volume = volume;
    }
  }, [volume]);

  // Handle music playing state changes
  useEffect(() => {
    if (!bgMusicRef.current || !isInitialized) return;

    if (isMusicPlaying) {
      bgMusicRef.current.play().catch((err) => {
        console.log('Play prevented:', err);
        setIsMusicPlaying(false);
      });
    } else {
      bgMusicRef.current.pause();
    }
  }, [isMusicPlaying, isInitialized]);

  const setVolume = useCallback((newVolume: number) => {
    setVolumeState(newVolume);
    localStorage.setItem('dw_music_volume', newVolume.toString());
  }, []);

  const playMusic = useCallback(() => {
    setIsMusicPlaying(true);
  }, []);

  const pauseMusic = useCallback(() => {
    setIsMusicPlaying(false);
  }, []);

  const toggleMusic = useCallback(() => {
    setIsMusicPlaying(prev => !prev);
  }, []);

  const changeTrack = useCallback((track: MusicTrack) => {
    if (track === currentTrack) return;
    setCurrentTrack(track);
  }, [currentTrack]);

  // Sound effects
  const playSound = useCallback((soundFile: string) => {
    if (!soundEnabled) return;
    const sound = new Audio(soundFile);
    sound.volume = volume;
    sound.play().catch(console.error);
  }, [soundEnabled, volume]);

  const playDiceSound = useCallback(() => {
    playSound('/sounds/dice-roll.mp3');
  }, [playSound]);

  const playHitSound = useCallback(() => {
    playSound('/sounds/hit.mp3');
  }, [playSound]);

  const playVictorySound = useCallback(() => {
    if (!soundEnabled) return;
    const sound = new Audio('/sounds/DW victory.mp3');
    sound.volume = volume;
    sound.play().catch(console.error);
    // Stop after 5 seconds
    setTimeout(() => {
      sound.pause();
      sound.currentTime = 0;
    }, 5000);
  }, [soundEnabled, volume]);

  const toggleSoundEnabled = useCallback(() => {
    const newValue = !soundEnabled;
    setSoundEnabledState(newValue);
    localStorage.setItem('dw_sound_enabled', newValue.toString());
  }, [soundEnabled]);

  return (
    <AudioContext.Provider
      value={{
        isMusicPlaying,
        currentTrack,
        volume,
        soundEnabled,
        toggleMusic,
        playMusic,
        pauseMusic,
        changeTrack,
        setVolume,
        toggleSoundEnabled,
        playDiceSound,
        playHitSound,
        playVictorySound,
      }}
    >
      {children}
    </AudioContext.Provider>
  );
}

export function useAudio() {
  const context = useContext(AudioContext);
  if (!context) {
    throw new Error('useAudio must be used within AudioProvider');
  }
  return context;
}
